import { useMemo } from "react";
import { Button, Card, Empty } from "antd";
import { useTranslation } from "react-i18next";
import { Line, Pie } from "@ant-design/charts";
import "./ChartsRow.scss";

const EMPTY_ARRAY = [];

/**
 * Vacio que dice EN QUE PERIODO no hay nada, y ofrece salir de el.
 *
 * "Sin datos de evolucion" a secas se lee como una averia. La mayoria de las
 * veces no lo es: es que el dashboard abre en 30 dias y ahi no hay nada.
 */
function VacioDePeriodo({ texto, timeRangeLabel, onVerTodo, t }) {
  return (
    <Empty
      description={
        <>
          <div>
            {timeRangeLabel
              ? t("charts.vacioEnPeriodo", { periodo: timeRangeLabel })
              : texto}
          </div>
          {onVerTodo ? (
            <Button type="link" size="small" onClick={onVerTodo}>
              {t("activity.verTodo")}
            </Button>
          ) : null}
        </>
      }
    />
  );
}

export function ChartsRow({ loading, charts, timeRangeLabel, onVerTodo }) {
  // Los nombres de las categorias que se pintan en la leyenda vienen de la base
  // de datos: son dato, no interfaz. Aqui solo se traducen los rotulos.
  const { t } = useTranslation("motricity");
  const evolution = charts?.evolution ?? EMPTY_ARRAY;

  // El servidor manda `name: null` cuando la sesion no tiene categoria. El
  // rotulo de ese caso lo pone el portal, que es quien tiene el diccionario:
  // en el servidor no hay forma de escribirlo en el idioma del que pregunta.
  const distribution = useMemo(() => {
    const bruto = charts?.distribution ?? EMPTY_ARRAY;
    return bruto.map((it) =>
      it?.name ? it : { ...it, name: t("charts.distribucion.sinCategoria") },
    );
  }, [charts?.distribution, t]);

  const distributionColorMap = useMemo(() => {
    const map = {};
    for (const it of distribution) {
      if (it?.name && it?.color) map[it.name] = it.color;
    }
    return map;
  }, [distribution]);

  const distributionTotal = useMemo(() => {
    return distribution.reduce((acc, it) => acc + (it?.value || 0), 0);
  }, [distribution]);

  const pieConfig = useMemo(
    () => ({
      data: distribution,
      angleField: "value",
      colorField: "name",
      radius: 1,
      innerRadius: 0.64,
      legend: { position: "bottom" },
      color: ({ name }) => distributionColorMap[name] || "#8C8C8C",
      statistic: {
        // El rotulo del centro del donut SI es interfaz, aunque estuviera
        // escrito aqui dentro en castellano.
        title: { formatter: () => t("charts.distribucion.centro") },
        content: { formatter: () => String(distributionTotal) },
      },
      tooltip: { showTitle: false },
      interactions: [{ type: "element-active" }],
      autoFit: true,
      height: 300,
    }),
    [distribution, distributionColorMap, distributionTotal, t],
  );

  const lineConfig = useMemo(
    () => ({
      data: evolution,
      xField: "week",
      yField: "score",
      smooth: true,
      autoFit: true,
      height: 300,
      point: { size: 4, shape: "circle" },
      yAxis: { min: 0 },
      color: "#1677ff",
      tooltip: {
        showMarkers: true,
        formatter: (datum) => ({
          name: "Puntuacion",
          value: datum.score,
        }),
      },
      area: {
        style: { fillOpacity: 0.08 },
      },
    }),
    [evolution],
  );

  return (
    <div className="revi-dashboard-charts">
      <Card
        className="revi-dashboard-charts__card"
        variant="outlined"
        title={t("charts.evolucion.titulo")}
        loading={loading}
      >
        {!loading && evolution.length === 0 ? (
          <VacioDePeriodo
            texto={t("charts.evolucion.vacio")}
            timeRangeLabel={timeRangeLabel}
            onVerTodo={onVerTodo}
            t={t}
          />
        ) : (
          <Line {...lineConfig} />
        )}
      </Card>

      <Card
        className="revi-dashboard-charts__card"
        variant="outlined"
        title={t("charts.distribucion.titulo")}
        loading={loading}
      >
        {!loading && distribution.length === 0 ? (
          <VacioDePeriodo
            texto={t("charts.distribucion.vacio")}
            timeRangeLabel={timeRangeLabel}
            onVerTodo={onVerTodo}
            t={t}
          />
        ) : (
          <Pie {...pieConfig} />
        )}
      </Card>
    </div>
  );
}
